'use client';

import { useState } from 'react';
import { Play } from 'lucide-react';
import { MARKETING_TOKENS as T } from './marketingTokens';
import { thumbnailUrl, thumbnailFallbackUrl, embedUrl } from '@/lib/howItWorksVideos';

/**
 * A YouTube embed that does not touch YouTube's player until you ask it to.
 *
 * Until the visitor clicks we render only the thumbnail image and a real
 * button: no player script, no youtube-nocookie request, no YouTube cookie.
 * With twenty-six recordings on /how-it-works, twenty-six eager iframes would
 * be several megabytes of player before anyone has watched a second of it.
 * The thumbnail itself is still third-party content from i.ytimg.com — the
 * cookie policy says exactly that.
 *
 * The facade is a <button>, not a clickable <div>, so it is reachable and
 * operable from the keyboard with no extra handlers. The image is alt="" on
 * purpose: the button already announces the title, and an alt would make a
 * screen reader read it twice.
 *
 * There is deliberately no size prop. Every recording on /how-it-works is a
 * product tutorial and renders at the same size; the grid decides how wide that
 * is. A "supporting" variant made some tutorials look less worth watching.
 */
export default function VideoEmbed({
  id,
  title,
  /** Set on the dark band so the frame edge reads against it. */
  onDark = false,
}: {
  id: string;
  title: string;
  onDark?: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const [thumb, setThumb] = useState(thumbnailUrl(id));

  return (
    <div
      className="hiw-frame relative w-full overflow-hidden rounded-[6px]"
      style={{
        aspectRatio: '16 / 9',
        backgroundColor: onDark ? 'rgba(255,255,255,0.06)' : T.SURFACE_ALT,
        border: `1px solid ${onDark ? 'rgba(255,255,255,0.16)' : T.BORDER}`,
      }}
    >
      {playing ? (
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`${embedUrl(id)}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ border: 0 }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${title}`}
          className="hiw-play group absolute inset-0 h-full w-full cursor-pointer p-0"
          style={{ background: 'none', border: 0 }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={thumb}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setThumb(thumbnailFallbackUrl(id))}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <span
            aria-hidden
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.34) 100%)' }}
          />
          <span
            aria-hidden
            className="hiw-play-mark absolute left-1/2 top-1/2 flex items-center justify-center rounded-full"
            style={{
              width: 76,
              height: 76,
              transform: 'translate(-50%,-50%)',
              backgroundColor: T.PRIMARY,
              color: T.ON_PRIMARY,
            }}
          >
            <Play style={{ width: 28, height: 28, marginLeft: 3 }} fill="currentColor" strokeWidth={0} />
          </span>
        </button>
      )}
    </div>
  );
}
